import type { Level } from "@-/dev.improve.haniverse.lib.level";
import { Pressable, View } from "@-/com.npmjs.react-native";
import { useMemo, useState } from "react";
import { LegendList } from "@-/com.npmjs.legendapp__list";
import { Surface } from "@-/dev.improve.haniverse.components.ui.surface";
import {
  HorizontalScrollEdgeFadeMask,
  useHorizontalScrollEdges,
} from "@-/dev.improve.haniverse.components.ui.scroll-edge-fade";
import {
  getSimplifiedLevels,
  getTraditionalLevels,
} from "@-/dev.improve.haniverse.assets.graphemes.levels";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { LevelSurfaceHeader } from "@-/dev.improve.haniverse.routes.nav.app._components.level.surface";
import { useNewMorphemeLessonRevealPressMutation } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.new-morpheme._lib.reveal";
import { useMorphemePanelsHrefNavigator } from "@-/dev.improve.haniverse.lib.morpheme.page.navigator";
import { LessonLevelGlyphGrid } from "#lesson-level-glyph-grid";
import {
  getLessonLevelEdgeFadeDistance,
  getLessonLevelGridLayout,
  LESSON_LEVEL_GRID_INSET,
  getLessonLevelScrollOffset,
} from "#get-lesson-level-grid-layout";

type LevelStripSection = {
  characters: readonly string[];
  key: string;
  level: Level;
};

/** Bottom curriculum strip: one row across levels, initially positioned around this lesson. */
export function LessonLevelSurface({
  activeMorpheme,
  characters,
  description,
  label,
  level,
  maxHeight,
}: {
  activeMorpheme?: string;
  characters?: readonly string[];
  description?: string;
  label?: string;
  level: Level;
  maxHeight?: number;
}) {
  const morphemePanelsHrefNavigator = useMorphemePanelsHrefNavigator();
  const revealMutation = useNewMorphemeLessonRevealPressMutation();
  const { hanziScript } = useActorUser();
  const [width, setWidth] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);
  const edges = useHorizontalScrollEdges();
  const title = label ?? `Level ${level.index + 1}`;
  const part =
    characters !== undefined && characters === level.pronunciationCharacters
      ? "pronunciation"
      : "character";
  const activeKey = `${level.index}/${part}`;
  const sections = useMemo(() => {
    const curriculum =
      hanziScript === "traditional" ? getTraditionalLevels() : getSimplifiedLevels();
    const entries = curriculum.flatMap((entry): LevelStripSection[] => {
      const result = [
        {
          characters: entry.characters,
          key: `${entry.index}/character`,
          level: entry,
        },
      ];
      if (entry.pronunciationCharacters?.length) {
        result.push({
          characters: entry.pronunciationCharacters,
          key: `${entry.index}/pronunciation`,
          level: entry,
        });
      }
      return result;
    });
    const current = {
      characters: characters ?? level.characters,
      key: activeKey,
      level,
    };
    const index = entries.findIndex((entry) => entry.key === activeKey);
    if (index === -1) {
      return [current];
    }
    entries[index] = current;
    return entries;
  }, [activeKey, characters, hanziScript, level]);
  const activeSectionIndex = Math.max(
    0,
    sections.findIndex((section) => section.key === activeKey),
  );
  const rowMaxHeight = maxHeight === undefined ? undefined : maxHeight - headerHeight - 2;
  const layouts = useMemo(() => {
    let offset = 0;
    return sections.map((section, index) => {
      const grid = getLessonLevelGridLayout({
        count: section.characters.length,
        maxHeight: rowMaxHeight,
        width,
      });
      const layout = {
        ...grid,
        index,
        length: grid.gridWidth + LESSON_LEVEL_GRID_INSET * 2,
        offset,
      };
      offset += layout.length;
      return layout;
    });
  }, [rowMaxHeight, sections, width]);
  const currentLayout = layouts[activeSectionIndex];
  const activeIndex = (characters ?? level.characters).indexOf(activeMorpheme ?? "");
  const scrollOffset =
    (currentLayout?.offset ?? 0) +
    getLessonLevelScrollOffset({
      activeIndex,
      count: (characters ?? level.characters).length,
      tileSize: currentLayout?.tileSize ?? 1,
      width,
    });
  const updateEdgeFade = (offset: number, viewportWidth: number) => {
    const left = getLessonLevelEdgeFadeDistance(layouts, offset);
    const right = getLessonLevelEdgeFadeDistance(layouts, offset + viewportWidth);
    // Project the viewport's hidden distances onto each level's interior, so endpoint tiles
    // remain clear even where two levels meet in the continuous curriculum strip.
    edges.scrollX.set(left);
    edges.viewportWidth.set(viewportWidth);
    edges.contentWidth.set(left + viewportWidth + right);
  };
  const onNavigate = (morpheme: string, isCurrent: boolean) => {
    if (isCurrent && morpheme === activeMorpheme) {
      if (!revealMutation.isPending) {
        revealMutation.mutate();
      }
      return;
    }
    morphemePanelsHrefNavigator.push.mutate({
      morpheme,
      prefix: {
        href: "/(nav)/(app)/learn/explore/morpheme/[morpheme]",
        params: { view: "mnemonics" },
      },
    });
  };

  return (
    <Surface testID="lesson-level-surface">
      <View
        accessibilityLabel={description === undefined ? title : `${title} – ${description}`}
        accessible
        onLayout={(event) => setHeaderHeight(event.nativeEvent.layout.height)}
        testID="lesson-level-subtitle"
      >
        <LevelSurfaceHeader
          characters={characters}
          description={description}
          label={title}
          level={level}
        />
      </View>
      <HorizontalScrollEdgeFadeMask
        edges={edges}
        testID="lesson-level-body-fade"
        width={(currentLayout?.tileSize ?? 40) / 2}
      >
        <Pressable
          accessible={false}
          className="py-1"
          onLayout={(event) => {
            setWidth(event.nativeEvent.layout.width);
            edges.onLayout(event);
          }}
          onPress={() => {}}
        >
          {width > 0 ? (
            <LegendList
              className="grow-0"
              data={sections}
              drawDistance={width}
              getFixedItemSize={(_item, index) => layouts[index]?.length}
              horizontal
              initialScrollOffset={scrollOffset}
              key={`${activeKey}/${activeIndex}/${width}`}
              keyExtractor={(section) => section.key}
              onLoad={() => updateEdgeFade(scrollOffset, width)}
              onScroll={(event) =>
                updateEdgeFade(
                  event.nativeEvent.contentOffset.x,
                  event.nativeEvent.layoutMeasurement.width,
                )
              }
              // Each level row owns hooks keyed to its level, and renderItem closes over the active
              // card, so a row is remounted rather than reused for another level.
              recycleItems={false}
              renderItem={({ index, item }) => (
                <View style={{ width: layouts[index]?.length }}>
                  <LessonLevelGlyphGrid
                    activeMorpheme={item.key === activeKey ? activeMorpheme : undefined}
                    characters={item.characters}
                    className={index > activeSectionIndex ? "opacity-15" : undefined}
                    hanziScript={hanziScript}
                    level={item.level}
                    maxHeight={rowMaxHeight}
                    onNavigate={(morpheme) => onNavigate(morpheme, item.key === activeKey)}
                    viewportWidth={width}
                  />
                </View>
              )}
              scrollEventThrottle={16}
              showsHorizontalScrollIndicator={false}
              style={{ height: currentLayout?.tileSize ?? 40 }}
              testID="lesson-level-strip"
            />
          ) : null}
        </Pressable>
      </HorizontalScrollEdgeFadeMask>
    </Surface>
  );
}
